import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Clock, Plus, Trash2, UserPlus, Euro, TrendingUp, Loader2, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const BULLETIN_STATUTS = ["Approuvé", "En attente", "Invalide", "Annulé"];
const STATUT_TONE = {
  "Approuvé": "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
  "En attente": "bg-[rgba(252,219,83,0.4)] text-[#5A4200]",
  "Invalide": "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
  "Annulé": "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
};

function hoursBetween(a, b) {
  if (!a || !b) return null;
  const [h1, m1] = a.split(":").map(Number);
  const [h2, m2] = b.split(":").map(Number);
  const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  return diff > 0 ? diff / 60 : null;
}

export function BulletinsDialog({ bookingId, open, onOpenChange }) {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extraRds, setExtraRds] = useState([]); // RDs ajoutés manuellement

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try { const { data } = await api.get(`/bulletins/booking/${bookingId}`); setSheet(data); }
    finally { setLoading(false); }
  }, [bookingId]);

  useEffect(() => { if (open) { setExtraRds([]); load(); } }, [open, load]);

  const booking = sheet?.booking;
  const presenceByRd = useMemo(() => {
    const m = {}; (sheet?.presences || []).forEach((p) => { m[p.rd_id] = p; }); return m;
  }, [sheet]);
  const bulletinsByRd = useMemo(() => {
    const m = {}; (sheet?.bulletins || []).forEach((b) => { (m[b.rd_id] = m[b.rd_id] || []).push(b); }); return m;
  }, [sheet]);

  const rdList = useMemo(() => {
    const base = sheet?.rds || [];
    const ids = new Set(base.map((r) => r.id));
    return [...base, ...extraRds.filter((r) => !ids.has(r.id))];
  }, [sheet, extraRds]);

  const totalBull = sheet?.bulletins?.length || 0;
  const totalMontant = (sheet?.bulletins || []).reduce((s, b) => s + (b.montant || 0), 0);
  const presentCount = Object.keys(presenceByRd).length;

  const setPresent = async (rd, on) => {
    if (on) {
      await api.post("/bulletins/presence", { booking_id: bookingId, rd_id: rd.id });
    } else {
      const p = presenceByRd[rd.id];
      if (p) await api.delete(`/bulletins/presence/${p.id}`);
    }
    load();
  };
  const saveHours = async (rd, field, value) => {
    const p = presenceByRd[rd.id] || {};
    await api.post("/bulletins/presence", {
      booking_id: bookingId, rd_id: rd.id,
      heure_debut: field === "heure_debut" ? value : p.heure_debut,
      heure_fin: field === "heure_fin" ? value : p.heure_fin,
    });
    load();
  };
  const addBulletin = async (rd, { montant, statut, heure }) => {
    await api.post("/bulletins", { booking_id: bookingId, rd_id: rd.id, montant, statut, heure });
    load();
  };
  const removeBulletin = async (id) => { await api.delete(`/bulletins/${id}`); load(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulletins-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Bulletins & présence — {booking?.spot_name || "Journée-spot"}</DialogTitle>
          <DialogDescription>
            {booking?.date ? `Journée du ${booking.date}. ` : ""}Cochez les RD présents, saisissez leurs heures propres (créneaux libres) et leurs bulletins.
          </DialogDescription>
        </DialogHeader>

        {loading && !sheet ? (
          <div className="flex items-center justify-center py-10 text-muted-ink"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <SummaryChip icon={Users} label="RD présents" value={presentCount} testId="bull-sum-present" />
              <SummaryChip icon={TrendingUp} label="Bulletins" value={totalBull} testId="bull-sum-count" />
              <SummaryChip icon={Euro} label="Montant" value={`${Math.round(totalMontant)} €`} testId="bull-sum-montant" />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-muted-ink">Recruteurs</span>
              <RdPicker onPick={(rd) => setExtraRds((l) => [...l, rd])} />
            </div>

            <ScrollArea className="h-[380px] pr-2" data-testid="bull-rd-list">
              <div className="space-y-2.5">
                {rdList.length === 0 && (
                  <div className="rounded-lg bg-[var(--surface-2)] px-3 py-4 text-center text-xs text-muted-ink">
                    Aucun RD rattaché à cette ligne. Ajoutez un RD présent ci-dessus.
                  </div>
                )}
                {rdList.map((rd) => (
                  <RdCard key={rd.id} rd={rd} presence={presenceByRd[rd.id]} bulletins={bulletinsByRd[rd.id] || []}
                    onPresent={setPresent} onHours={saveHours} onAdd={addBulletin} onRemove={removeBulletin} />
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryChip({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid={testId}>
      <Icon className="h-4 w-4 text-[var(--shariti-orange)]" />
      <div><div className="text-sm font-extrabold leading-none">{value}</div><div className="text-[10px] text-muted-ink">{label}</div></div>
    </div>
  );
}

function RdCard({ rd, presence, bulletins, onPresent, onHours, onAdd, onRemove }) {
  const present = !!presence;
  const hrs = hoursBetween(presence?.heure_debut, presence?.heure_fin);
  const perHour = hrs ? (bulletins.length / hrs).toFixed(1) : null;
  const [montant, setMontant] = useState("");
  const [statut, setStatut] = useState("Approuvé");
  const [heure, setHeure] = useState("");
  const [adding, setAdding] = useState(false);

  const submit = async () => {
    setAdding(true);
    try { await onAdd(rd, { montant: montant || null, statut, heure: heure || null }); setMontant(""); setHeure(""); }
    catch { toast.error("Erreur lors de l'ajout"); }
    finally { setAdding(false); }
  };

  return (
    <div className={`rounded-xl border p-3 transition-colors ${present ? "border-[var(--shariti-orange)]/40 bg-[rgba(238,114,46,0.03)]" : "border-[var(--border-soft)] bg-white"}`} data-testid={`bull-rd-${rd.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="break-words text-sm font-bold">{rd.name}</span>
            {rd.code && <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px]">{rd.code}</Badge>}
            {rd.statut === "a_completer" && <Badge className="bg-[rgba(233,184,0,0.35)] text-[#5A4200] text-[10px]">à compléter</Badge>}
          </div>
          {present && (
            <div className="mt-0.5 text-[11px] text-muted-ink">
              {bulletins.length} bull.{perHour ? <span className="font-bold text-[var(--shariti-orange)]"> · {perHour}/h</span> : (hrs === null && (presence?.heure_debut || presence?.heure_fin) ? " · heures incomplètes" : "")}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-muted-ink">Présent</span>
          <Switch checked={present} onCheckedChange={(v) => onPresent(rd, v)} data-testid={`bull-present-${rd.id}`} />
        </div>
      </div>

      {present && (
        <div className="mt-2.5 space-y-2.5 border-t border-[var(--border-soft)] pt-2.5">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-muted-ink" />
            <Input type="time" defaultValue={presence?.heure_debut || ""} onBlur={(e) => onHours(rd, "heure_debut", e.target.value)}
              className="h-8 w-28 text-xs" data-testid={`bull-debut-${rd.id}`} />
            <span className="text-xs text-muted-ink">→</span>
            <Input type="time" defaultValue={presence?.heure_fin || ""} onBlur={(e) => onHours(rd, "heure_fin", e.target.value)}
              className="h-8 w-28 text-xs" data-testid={`bull-fin-${rd.id}`} />
          </div>

          {bulletins.length > 0 && (
            <div className="space-y-1">
              {bulletins.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg bg-[var(--surface-2)] px-2.5 py-1.5 text-xs" data-testid={`bull-item-${b.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{b.montant != null ? `${b.montant} €` : "—"}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUT_TONE[b.statut] || "bg-white"}`}>{b.statut || "—"}</span>
                    {b.horodatage && <span className="text-[10px] text-muted-ink">{b.horodatage.slice(11, 16)}</span>}
                  </div>
                  <button onClick={() => onRemove(b.id)} data-testid={`bull-del-${b.id}`} className="text-[#D64545] hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2">
            <div className="w-24"><label className="text-[10px] text-muted-ink">Montant €</label>
              <Input type="number" value={montant} onChange={(e) => setMontant(e.target.value)} placeholder="ex. 15" className="h-8 text-xs" data-testid={`bull-montant-${rd.id}`} /></div>
            <div className="w-36"><label className="text-[10px] text-muted-ink">Statut</label>
              <Select value={statut} onValueChange={setStatut}>
                <SelectTrigger className="h-8 text-xs" data-testid={`bull-statut-${rd.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{BULLETIN_STATUTS.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="w-24"><label className="text-[10px] text-muted-ink">Heure</label>
              <Input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} className="h-8 text-xs" data-testid={`bull-heure-${rd.id}`} /></div>
            <Button size="sm" onClick={submit} disabled={adding} className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid={`bull-add-${rd.id}`}>
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Ajouter</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RdPicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/rds", { params: { search: q || undefined, limit: 20 } });
      setResults(data);
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-[var(--border-soft)]" data-testid="bull-add-rd"><UserPlus className="mr-1.5 h-3.5 w-3.5" />Ajouter un RD</Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Rechercher un RD (nom ou code)…" value={q} onValueChange={setQ} data-testid="bull-rd-search" />
          <CommandList>
            <CommandEmpty>Aucun RD trouvé.</CommandEmpty>
            <CommandGroup>
              {results.map((rd) => (
                <CommandItem key={rd.id} value={rd.id} onSelect={() => { onPick(rd); setOpen(false); }} data-testid={`bull-rd-opt-${rd.id}`}>
                  <span className="text-xs font-semibold">{rd.name}</span>
                  {rd.code && <span className="ml-2 text-[10px] text-muted-ink">{rd.code}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
